import Konva from "konva";
import { Options, useHotkeys } from "react-hotkeys-hook";
import { useMapCanvasShapes } from "../../../contexts/MapCanvasContext";
import { useMapCanvasStage } from "../../../contexts/MapCanvasContext/useMapCanvasStage";
import { useMapEditorActions } from "../../../contexts/MapEditorContext";
import { useMapEditorSelection } from "../../../contexts/MapEditorContext";
import { WallWindow, WallShape, WallPathShape } from "../../../../../types";
import { useEditorShortcuts } from "../EditorShortcutContext/useEditorShortcuts";
import { GLOBAL_SHORTCUTS_SCOPE } from "../shortcuts";

/** Find the window on a WallShape closest to the cursor */
const findClosestWindowOnWall = (
  cursor: { x: number; y: number },
  wall: WallShape
): WallWindow | null => {
  if (!wall.windows || wall.windows.length === 0) return null;

  const dx = wall.x2 - wall.x;
  const dy = wall.y2 - wall.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length === 0) return wall.windows[0];

  let bestWindow: WallWindow | null = null;
  let bestDistSq = Infinity;

  for (const win of wall.windows) {
    const winX = wall.x + win.position * dx;
    const winY = wall.y + win.position * dy;
    const distSq = (cursor.x - winX) ** 2 + (cursor.y - winY) ** 2;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      bestWindow = win;
    }
  }

  return bestWindow;
};

/** Find the window on a WallPathShape closest to the cursor */
const findClosestWindowOnWallPath = (
  cursor: { x: number; y: number },
  path: WallPathShape
): { edgeIndex: number; windowId: string } | null => {
  const { vertices, closed } = path;
  const edgeCount = closed ? vertices.length : vertices.length - 1;

  let bestEdgeIndex = -1;
  let bestWindowId = "";
  let bestDistSq = Infinity;

  for (let i = 0; i < edgeCount; i++) {
    const edge = path.edges[i];
    if (!edge?.windows || edge.windows.length === 0) continue;

    const from = vertices[i];
    const to = vertices[(i + 1) % vertices.length];
    const dx = to.x - from.x;
    const dy = to.y - from.y;

    for (const win of edge.windows) {
      const winX = from.x + win.position * dx;
      const winY = from.y + win.position * dy;
      const distSq = (cursor.x - winX) ** 2 + (cursor.y - winY) ** 2;
      if (distSq < bestDistSq) {
        bestDistSq = distSq;
        bestEdgeIndex = i;
        bestWindowId = win.id;
      }
    }
  }

  if (bestEdgeIndex === -1) return null;
  return { edgeIndex: bestEdgeIndex, windowId: bestWindowId };
};

export const useRemoveWindowShortcut = (
  stageRef: React.RefObject<Konva.Stage | null>
) => {
  const { selectedIds } = useMapEditorSelection();
  const { updateShape } = useMapEditorActions();
  const { shapes } = useMapCanvasShapes();
  const { stagePositionRef } = useMapCanvasStage();
  const { shortcuts } = useEditorShortcuts();

  const selectedShape =
    selectedIds.length === 1
      ? shapes.find(
          (s) => s.id === selectedIds[0] && (s.type === "wall" || s.type === "wall-path")
        )
      : undefined;

  const shortcutOptions: Options = {
    scopes: [GLOBAL_SHORTCUTS_SCOPE],
    enabled: !!selectedShape,
    preventDefault: true
  };

  useHotkeys(
    shortcuts.global.removeWindow,
    () => {
      if (!selectedShape) return;

      const stage = stageRef.current;
      if (!stage) return;

      const pointerPos = stage.getPointerPosition();
      if (!pointerPos) return;

      const stagePosition = stagePositionRef.current;
      const cursor = {
        x: (pointerPos.x - stagePosition.x) / stagePosition.scale,
        y: (pointerPos.y - stagePosition.y) / stagePosition.scale
      };

      if (selectedShape.type === "wall") {
        const wall = selectedShape as WallShape;
        const closestWindow = findClosestWindowOnWall(cursor, wall);
        if (!closestWindow) return;
        const windows = (wall.windows || []).filter((w) => w.id !== closestWindow.id);
        updateShape(wall.id, { windows } as Partial<WallShape>);
      } else if (selectedShape.type === "wall-path") {
        const path = selectedShape as WallPathShape;
        const result = findClosestWindowOnWallPath(cursor, path);
        if (!result) return;
        const newEdges = [...path.edges];
        newEdges[result.edgeIndex] = {
          ...newEdges[result.edgeIndex],
          windows: (newEdges[result.edgeIndex].windows || []).filter(
            (w) => w.id !== result.windowId
          )
        };
        updateShape(path.id, { edges: newEdges } as Partial<WallPathShape>);
      }
    },
    shortcutOptions,
    [shortcuts.global.removeWindow, selectedShape]
  );
};
